import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Save,
  Send,
  Building2,
  Loader2,
  Wrench,
  HardHat,
  Package,
  Boxes,
  FileCheck,
  Camera,
  X,
  ChevronDown,
  ChevronUp,
  Calculator
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import TenantMobileLayout from './TenantMobileLayout';

const ITEM_TYPES = [
  { value: 'equipment', label: 'Asset', icon: Wrench },
  { value: 'labour', label: 'Labour', icon: HardHat },
  { value: 'attachment', label: 'Attachment', icon: Package },
  { value: 'material', label: 'Material', icon: Boxes },
  { value: 'custom', label: 'Custom', icon: FileCheck }
];

const UNITS = [
  { value: 'hour', label: 'Hour' },
  { value: 'day', label: 'Day' },
  { value: 'week', label: 'Week' },
  { value: 'each', label: 'Each' },
  { value: 'tonne', label: 'Tonne' },
  { value: 'm3', label: 'm³' },
  { value: 'km', label: 'km' },
  { value: 'load', label: 'Load' }
];

const TenantMobileQuoteEdit = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id && id !== 'new';
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [defaultTerms, setDefaultTerms] = useState('');
  
  // Reference data for dropdowns
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const [workerTypes, setWorkerTypes] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [materials, setMaterials] = useState([]);

  // Section expansion states
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    description: false,
    lineItems: true,
    photos: false,
    terms: false
  });

  const [formData, setFormData] = useState({
    customer_id: '',
    site_id: '',
    pricing_type: 'hourly',
    valid_until: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
    description: '',
    line_items: [],
    terms_and_conditions: '',
    notes: '',
    photos: []
  });

  const [totals, setTotals] = useState({
    subtotal: 0,
    tax_rate: 10,
    tax_amount: 0,
    total: 0
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [
          customersRes, 
          equipmentTypesRes, 
          workerTypesRes, 
          attachmentTypesRes,
          materialsRes,
          termsRes
        ] = await Promise.all([
          api.get('/customers'),
          api.get('/equipment-types'),
          api.get('/worker-types'),
          api.get('/attachment-types').catch(() => ({ data: [] })),
          api.get('/tips').catch(() => ({ data: [] })),
          api.get('/quotes/settings/terms')
        ]);
        
        setCustomers(customersRes.data.filter(c => c.is_active !== false));
        setEquipmentTypes(equipmentTypesRes.data || []);
        setWorkerTypes(workerTypesRes.data || []);
        setAttachmentTypes(attachmentTypesRes.data || []);
        setMaterials(materialsRes.data || []);
        setDefaultTerms(termsRes.data.default_quote_terms || '');

        if (isEditing) {
          const quoteRes = await api.get(`/quotes/${id}`);
          const quote = quoteRes.data;
          setFormData({
            customer_id: quote.customer_id,
            site_id: quote.site_id || '',
            pricing_type: quote.pricing_type,
            valid_until: quote.valid_until,
            description: quote.description || '',
            line_items: quote.line_items || [],
            terms_and_conditions: quote.terms_and_conditions || '',
            notes: quote.notes || '',
            photos: quote.photos || []
          });
          setTotals({
            subtotal: quote.subtotal,
            tax_rate: quote.tax_rate,
            tax_amount: quote.tax_amount,
            total: quote.total
          });
          
          if (quote.customer_id) {
            const sitesRes = await api.get(`/sites?customer_id=${quote.customer_id}`);
            setSites(sitesRes.data);
          }
        } else {
          setFormData(prev => ({
            ...prev,
            terms_and_conditions: termsRes.data.default_quote_terms || ''
          }));
        }
      } catch (error) {
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, id, isEditing]);

  useEffect(() => {
    const subtotal = formData.line_items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.rate || 0));
    }, 0);
    const tax_amount = Math.round(subtotal * (totals.tax_rate / 100) * 100) / 100;
    const total = Math.round((subtotal + tax_amount) * 100) / 100;

    setTotals(prev => ({
      ...prev,
      subtotal: Math.round(subtotal * 100) / 100,
      tax_amount,
      total
    }));
  }, [formData.line_items, totals.tax_rate]);

  const handleCustomerChange = async (customerId) => {
    setFormData(prev => ({ ...prev, customer_id: customerId, site_id: '' }));
    
    if (customerId) {
      try {
        const sitesRes = await api.get(`/sites?customer_id=${customerId}`);
        setSites(sitesRes.data);
      } catch (error) {
        setSites([]);
      }
    } else {
      setSites([]);
    }
  };

  const addLineItem = () => {
    setFormData(prev => ({
      ...prev,
      line_items: [
        ...prev.line_items,
        {
          id: uuidv4(),
          item_type: 'equipment',
          description: '',
          equipment_id: '',
          equipment_type: '',
          worker_type_id: '',
          attachment_type_id: '',
          material_id: '',
          quantity: 1,
          unit: 'hour',
          rate: 0,
          total: 0,
          notes: ''
        }
      ]
    }));
    setExpandedSections(prev => ({ ...prev, lineItems: true }));
  };

  const updateLineItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      if (field === 'quantity' || field === 'rate') {
        newItems[index].total = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
      }
      
      if (field === 'equipment_type' && value) {
        newItems[index].description = value;
      }
      if (field === 'worker_type_id' && value) {
        const selected = workerTypes.find(w => w.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      if (field === 'attachment_type_id' && value) {
        const selected = attachmentTypes.find(a => a.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      if (field === 'material_id' && value) {
        const selected = materials.find(m => m.id === value);
        if (selected) newItems[index].description = selected.name || '';
      }
      
      if (field === 'item_type') {
        newItems[index].equipment_id = '';
        newItems[index].equipment_type = '';
        newItems[index].worker_type_id = '';
        newItems[index].attachment_type_id = '';
        newItems[index].material_id = '';
        newItems[index].description = '';
      }
      
      return { ...prev, line_items: newItems };
    });
  };

  const removeLineItem = (index) => {
    setFormData(prev => ({
      ...prev,
      line_items: prev.line_items.filter((_, i) => i !== index)
    }));
  };

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    const newPhotos = [];

    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await api.post('/quotes/upload-photo', formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data.url) {
          newPhotos.push(response.data.url);
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }

    if (newPhotos.length > 0) {
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, ...newPhotos]
      }));
      toast.success(`Uploaded ${newPhotos.length} photo(s)`);
    }
    
    setUploading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removePhoto = (index) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async (sendAfterSave = false) => {
    if (!formData.customer_id) {
      toast.error('Please select a customer');
      return;
    }
    
    if (formData.line_items.length === 0) {
      toast.error('Please add at least one line item');
      return;
    }

    setSaving(true);
    try {
      let savedQuote;
      
      if (isEditing) {
        const response = await api.put(`/quotes/${id}`, formData);
        savedQuote = response.data;
        toast.success('Quote updated');
      } else {
        const response = await api.post('/quotes', formData);
        savedQuote = response.data;
        toast.success('Quote created');
      }

      if (sendAfterSave && savedQuote) {
        await api.post(`/quotes/${savedQuote.id}/send`);
        toast.success('Quote sent to customer');
      }

      navigate('/tenant-mobile/quotes');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || '';
  };

  const renderLineItemTypeSelector = (item, index) => {
    const itemType = item.item_type || 'equipment';
    
    switch (itemType) {
      case 'equipment':
        return (
          <Select
            value={item.equipment_type || ''}
            onValueChange={(v) => updateLineItem(index, 'equipment_type', v)}
          >
            <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white">
              <SelectValue placeholder="Select equipment type" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a2a2a] border-gray-600">
              {equipmentTypes.map((type) => (
                <SelectItem key={type} value={type} className="text-white hover:bg-[#3a3a3a]">
                  {type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'labour':
        return (
          <Select
            value={item.worker_type_id || ''}
            onValueChange={(v) => updateLineItem(index, 'worker_type_id', v)}
          >
            <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white">
              <SelectValue placeholder="Select worker type" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a2a2a] border-gray-600">
              {workerTypes.map((type) => (
                <SelectItem key={type.id} value={type.id} className="text-white hover:bg-[#3a3a3a]">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'attachment':
        return (
          <Select
            value={item.attachment_type_id || ''}
            onValueChange={(v) => updateLineItem(index, 'attachment_type_id', v)}
          >
            <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white">
              <SelectValue placeholder="Select attachment" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a2a2a] border-gray-600">
              {attachmentTypes.map((type) => (
                <SelectItem key={type.id} value={type.id} className="text-white hover:bg-[#3a3a3a]">
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'material':
        return (
          <Select
            value={item.material_id || ''}
            onValueChange={(v) => updateLineItem(index, 'material_id', v)}
          >
            <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white">
              <SelectValue placeholder="Select material" />
            </SelectTrigger>
            <SelectContent className="bg-[#2a2a2a] border-gray-600">
              {materials.map((m) => (
                <SelectItem key={m.id} value={m.id} className="text-white hover:bg-[#3a3a3a]">
                  {m.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <TenantMobileLayout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </TenantMobileLayout>
    );
  }

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/tenant-mobile/quotes')}
            className="text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-white font-bold text-lg flex-1">
            {isEditing ? 'Edit Quote' : 'New Quote'}
          </h1>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-32">
        {/* Customer Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('customer')}
          >
            <div className="flex items-center gap-2">
              <Building2 className="h-5 w-5 text-[#ffd108]" />
              <span className="text-white font-medium">Customer Details</span>
              {formData.customer_id && (
                <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                  {getCustomerName(formData.customer_id)}
                </Badge>
              )}
            </div>
            {expandedSections.customer ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.customer && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              <div>
                <Label className="text-gray-400 text-sm">Customer *</Label>
                <Select value={formData.customer_id} onValueChange={handleCustomerChange}>
                  <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white mt-1">
                    <SelectValue placeholder="Select customer" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] border-gray-600 max-h-60">
                    {customers.map((customer) => (
                      <SelectItem 
                        key={customer.id} 
                        value={customer.id}
                        className="text-white hover:bg-[#3a3a3a]"
                      >
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {sites.length > 0 && (
                <div>
                  <Label className="text-gray-400 text-sm">Site (Optional)</Label>
                  <Select 
                    value={formData.site_id} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}
                  >
                    <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white mt-1">
                      <SelectValue placeholder="Select site" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a2a2a] border-gray-600 max-h-60">
                      {sites.map((site) => (
                        <SelectItem 
                          key={site.id} 
                          value={site.id}
                          className="text-white hover:bg-[#3a3a3a]"
                        >
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-400 text-sm">Pricing Type</Label>
                  <Select 
                    value={formData.pricing_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, pricing_type: v }))}
                  >
                    <SelectTrigger className="bg-[#3a3a3a] border-gray-600 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a2a2a] border-gray-600">
                      <SelectItem value="hourly" className="text-white hover:bg-[#3a3a3a]">Hourly</SelectItem>
                      <SelectItem value="daily" className="text-white hover:bg-[#3a3a3a]">Daily</SelectItem>
                      <SelectItem value="fixed" className="text-white hover:bg-[#3a3a3a]">Fixed Price</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-sm">Valid Until</Label>
                  <Input
                    type="date"
                    value={formData.valid_until}
                    onChange={(e) => setFormData(prev => ({ ...prev, valid_until: e.target.value }))}
                    className="bg-[#3a3a3a] border-gray-600 text-white mt-1"
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Description Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('description')}
          >
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[#ffd108]" />
              <span className="text-white font-medium">Work Description</span>
            </div>
            {expandedSections.description ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.description && (
            <CardContent className="pt-0 pb-4 px-4">
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe the work to be performed..."
                className="bg-[#3a3a3a] border-gray-600 text-white min-h-[100px]"
              />
            </CardContent>
          )}
        </Card>

        {/* Line Items Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('lineItems')}
          >
            <div className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-[#ffd108]" />
              <span className="text-white font-medium">Line Items</span>
              <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                {formData.line_items.length}
              </Badge>
            </div>
            {expandedSections.lineItems ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.lineItems && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              {formData.line_items.map((item, index) => {
                const TypeIcon = ITEM_TYPES.find(t => t.value === item.item_type)?.icon || FileCheck;
                return (
                  <div key={item.id} className="bg-[#3a3a3a] rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-[#ffd108]" />
                        <span className="text-gray-400 text-sm">Item {index + 1}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeLineItem(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/20 h-8 w-8"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div>
                      <Label className="text-gray-400 text-xs">Type</Label>
                      <Select 
                        value={item.item_type} 
                        onValueChange={(v) => updateLineItem(index, 'item_type', v)}
                      >
                        <SelectTrigger className="bg-[#2a2a2a] border-gray-600 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#2a2a2a] border-gray-600">
                          {ITEM_TYPES.map((type) => {
                            const Icon = type.icon;
                            return (
                              <SelectItem 
                                key={type.value} 
                                value={type.value}
                                className="text-white hover:bg-[#3a3a3a]"
                              >
                                <div className="flex items-center gap-2">
                                  <Icon className="h-4 w-4" />
                                  {type.label}
                                </div>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>

                    {item.item_type !== 'custom' && (
                      <div>
                        <Label className="text-gray-400 text-xs">
                          {item.item_type === 'equipment' ? 'Equipment Type' :
                           item.item_type === 'labour' ? 'Worker Type' :
                           item.item_type === 'attachment' ? 'Attachment' : 'Material'}
                        </Label>
                        <div className="mt-1">
                          {renderLineItemTypeSelector(item, index)}
                        </div>
                      </div>
                    )}

                    <div>
                      <Label className="text-gray-400 text-xs">Description</Label>
                      <Input
                        value={item.description}
                        onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                        placeholder="Item description"
                        className="bg-[#2a2a2a] border-gray-600 text-white mt-1"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <Label className="text-gray-400 text-xs">Qty</Label>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateLineItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="bg-[#2a2a2a] border-gray-600 text-white mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs">Unit</Label>
                        <Select 
                          value={item.unit} 
                          onValueChange={(v) => updateLineItem(index, 'unit', v)}
                        >
                          <SelectTrigger className="bg-[#2a2a2a] border-gray-600 text-white mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[#2a2a2a] border-gray-600">
                            {UNITS.map((unit) => (
                              <SelectItem 
                                key={unit.value} 
                                value={unit.value}
                                className="text-white hover:bg-[#3a3a3a]"
                              >
                                {unit.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-gray-400 text-xs">Rate ($)</Label>
                        <Input
                          type="number"
                          value={item.rate}
                          onChange={(e) => updateLineItem(index, 'rate', parseFloat(e.target.value) || 0)}
                          className="bg-[#2a2a2a] border-gray-600 text-white mt-1"
                        />
                      </div>
                    </div>

                    <div className="flex justify-end">
                      <span className="text-[#ffd108] font-medium">
                        ${((item.quantity || 0) * (item.rate || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                );
              })}

              <Button
                variant="outline"
                onClick={addLineItem}
                className="w-full bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a] hover:border-[#ffd108]"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Line Item
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Photos Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('photos')}
          >
            <div className="flex items-center gap-2">
              <Camera className="h-5 w-5 text-[#ffd108]" />
              <span className="text-white font-medium">Reference Photos</span>
              {formData.photos.length > 0 && (
                <Badge className="bg-[#ffd108]/20 text-[#ffd108] ml-2">
                  {formData.photos.length}
                </Badge>
              )}
            </div>
            {expandedSections.photos ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.photos && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {formData.photos.map((photo, index) => (
                    <div key={index} className="relative group aspect-square">
                      <img
                        src={photo}
                        alt={`Reference ${index + 1}`}
                        className="w-full h-full object-cover rounded-lg"
                      />
                      <button
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 bg-red-500 rounded-full p-1"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                type="file"
                ref={fileInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                multiple
                className="hidden"
              />
              
              <Button
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a]"
              >
                {uploading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="h-4 w-4 mr-2" />
                )}
                {uploading ? 'Uploading...' : 'Add Photos'}
              </Button>
            </CardContent>
          )}
        </Card>

        {/* Terms Section */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <button 
            className="w-full p-4 flex items-center justify-between"
            onClick={() => toggleSection('terms')}
          >
            <div className="flex items-center gap-2">
              <FileCheck className="h-5 w-5 text-[#ffd108]" />
              <span className="text-white font-medium">Terms & Notes</span>
            </div>
            {expandedSections.terms ? (
              <ChevronUp className="h-5 w-5 text-gray-400" />
            ) : (
              <ChevronDown className="h-5 w-5 text-gray-400" />
            )}
          </button>
          
          {expandedSections.terms && (
            <CardContent className="pt-0 pb-4 px-4 space-y-4">
              <div>
                <Label className="text-gray-400 text-sm">Terms & Conditions</Label>
                <Textarea
                  value={formData.terms_and_conditions}
                  onChange={(e) => setFormData(prev => ({ ...prev, terms_and_conditions: e.target.value }))}
                  placeholder="Enter terms and conditions..."
                  className="bg-[#3a3a3a] border-gray-600 text-white min-h-[100px] mt-1"
                />
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Internal Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Notes (not visible to customer)..."
                  className="bg-[#3a3a3a] border-gray-600 text-white min-h-[80px] mt-1"
                />
              </div>
            </CardContent>
          )}
        </Card>

        {/* Totals Summary */}
        <Card className="bg-[#2a2a2a] border-[#ffd108]/30">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span>${totals.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between items-center text-gray-400">
                <span>GST ({totals.tax_rate}%)</span>
                <span>${totals.tax_amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-gray-600 pt-2 flex justify-between text-white font-bold text-lg">
                <span>Total</span>
                <span className="text-[#ffd108]">${totals.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-16 left-0 right-0 bg-[#2a2a2a] border-t border-gray-700 p-4 z-10">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => handleSave(false)}
            disabled={saving}
            className="flex-1 bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a]"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save Draft
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex-1 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Save & Send
          </Button>
        </div>
      </div>
    </TenantMobileLayout>
  );
};

export default TenantMobileQuoteEdit;
